import OrganizationLogo from 'src/components/Organization/OrganizationLogo';
import { cn } from 'src/lib/utils';
import { IconLogo } from 'src/components/Svg/Images';

import { getAvatarColor, getAvatarColorClassName, getInitials } from '../model';

import type { IAppColor } from 'src/shared/entities';

export type AppAvatarShape = 'circle' | 'rounded-square';
export type AppAvatarSize = 'xs' | 'sm' | 'md' | 'lg';

type Props = {
  appId: string;
  name: string | null;
  claimed: boolean;
  shape: AppAvatarShape;
  size?: AppAvatarSize;
  logoUrl?: string;
  brandColor?: IAppColor;
};

const sizeClassNames: Record<
  AppAvatarSize,
  { box: string; mark: string; initials: string; shape: Record<AppAvatarShape, string> }
> = {
  xs: {
    box: 'size-5',
    mark: 'size-3',
    initials: 'text-2xs',
    shape: { circle: 'rounded-full', 'rounded-square': 'rounded-md' },
  },
  sm: {
    box: 'size-8',
    mark: 'size-4.5',
    initials: 'text-xs',
    shape: { circle: 'rounded-full', 'rounded-square': 'rounded-lg' },
  },
  md: {
    box: 'size-10',
    mark: 'size-5',
    initials: 'text-sm',
    shape: { circle: 'rounded-full', 'rounded-square': 'rounded-lg' },
  },
  lg: {
    box: 'size-13',
    mark: 'size-7',
    initials: '',
    shape: { circle: 'rounded-full', 'rounded-square': 'rounded-xl' },
  },
};

const AppAvatar = ({ appId, name, claimed, shape, size = 'lg', logoUrl, brandColor }: Props) => {
  const classNames = sizeClassNames[size];
  const shapeClassName = classNames.shape[shape];

  if (!claimed) {
    return (
      <div
        role="img"
        aria-label="App avatar"
        data-shape={shape}
        data-size={size}
        data-variant="unclaimed"
        className={cn('shrink-0 flex items-center justify-center bg-border', classNames.box, shapeClassName)}
      >
        <IconLogo className={cn('opacity-20 grayscale', classNames.mark)} />
      </div>
    );
  }

  const color = brandColor ?? getAvatarColor(appId);

  return (
    <div role="img" aria-label="App avatar" data-shape={shape} data-size={size} className="shrink-0">
      <OrganizationLogo
        name={name ?? ''}
        logo={logoUrl}
        shouldFetchLogo={false}
        fallbackText={getInitials(name ?? '')}
        classes={{
          fallback: cn(
            classNames.initials,
            logoUrl ? 'bg-white text-foreground' : cn('text-white', getAvatarColorClassName(color), shapeClassName),
          ),
        }}
        className={cn('shrink-0 border-0 font-semibold', classNames.box, shapeClassName, logoUrl && 'shadow-sm')}
      />
    </div>
  );
};

export { AppAvatar };
